import { useEffect, useState, useRef } from 'react';
import { X, Printer } from 'lucide-react';
import axios from 'axios';
import api, { documentsApi } from '../utils/api';
import InvoiceTemplate from './InvoiceTemplate';
import useEscClose from '../hooks/useEscClose';

export default function PrintPreviewModal({ documentId, onClose, emisor }) {
  useEscClose(onClose);

  const [documento, setDocumento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tipoCambio, setTipoCambio] = useState(1);
  const printRef = useRef();

  useEffect(() => {
    if (documentId) {
      loadDocument();
    }
  }, [documentId]);

  const loadDocument = async () => {
    setLoading(true);
    try {
      const response = await documentsApi.getById(documentId);
      const doc = response.data.data;
      setDocumento(doc);

      // Si es USD, obtener tipo de cambio
      const moneda = doc.codigo_moneda || doc.codigoMoneda || 'CRC';
      const docTipoCambio = parseFloat(doc.tipoCambio || doc.tipo_cambio) || 1;

      console.log('[Preview] Moneda:', moneda, 'TipoCambio en doc:', docTipoCambio);

      if (moneda === 'USD') {
        if (docTipoCambio > 1) {
          setTipoCambio(docTipoCambio);
        } else {
          // Leer TC de la tabla tipos_cambio del tenant (sincronizada con BCCR).
          // Ver project_finance360_tipo_cambio: el endpoint global /exchange-rate
          // cacheaba el wrapper externo por worker PM2 y servía valores stale random.
          try {
            const hoyCR = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Costa_Rica' });
            const tcResponse = await api.get(`/exchange-rate/${hoyCR}`);
            if (tcResponse.data?.success && tcResponse.data?.venta) {
              setTipoCambio(tcResponse.data.venta);
            } else {
              setTipoCambio(497); // Valor aproximado
            }
          } catch (tcError) {
            console.error('[Preview] Error fetching exchange rate:', tcError);
            setTipoCambio(497); // Valor aproximado por defecto
          }
        }
      }
    } catch (error) {
      console.error('Error loading document:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Imprimir - ${documento?.consecutivo || 'Documento'}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando documento...</p>
        </div>
      </div>
    );
  }

  if (!documento) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <p className="text-red-600">Error al cargar el documento</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-200 rounded">Cerrar</button>
        </div>
      </div>
    );
  }

  // Prepare data for InvoiceTemplate
  const lineas = documento.lineas || [];
  const moneda = documento.codigo_moneda || documento.codigoMoneda || 'CRC';

  const receptor = {
    nombre: documento.receptorNombre || documento.receptor_nombre,
    identificacion: documento.receptorIdentificacion || documento.receptor_identificacion,
    tipo_identificacion: documento.receptorTipoIdentificacion || documento.receptor_tipo_identificacion,
    email: documento.receptorEmail || documento.receptor_email,  // Email general de la empresa
    email_facturacion: documento.receptorEmailFacturacion || documento.receptor_email_facturacion,  // Email para FE
    telefono_codigo: documento.receptorTelefonoCodigo || documento.receptor_telefono_codigo || '506',
    telefono_numero: documento.receptorTelefonoNumero || documento.receptor_telefono_numero,
    otras_senas: documento.receptorOtrasSenas || documento.receptor_otras_senas,
    provincia: documento.receptorProvincia || documento.receptor_provincia,
    canton: documento.receptorCanton || documento.receptor_canton,
    distrito: documento.receptorDistrito || documento.receptor_distrito,
    dias_credito: documento.receptorDiasCredito || documento.receptor_dias_credito || documento.diasCredito || 30,
    actividad_economica: documento.receptorActividadEconomica || documento.receptor_actividad_economica
  };

  // Calculate totals from line items if not present in document
  const calcularTotales = () => {
    let totalGravado = 0;
    let totalExento = 0;
    let totalDescuentos = 0;
    let totalImpuesto = 0;
    let totalVenta = 0;

    // Para desglose de impuestos
    let totalIVA = 0;
    const impuestosAdicionalesMap = {};

    lineas.forEach(l => {
      const subtotal = parseFloat(l.subtotal || l.monto_total || (l.cantidad * l.precio_unitario) || 0);
      const descuento = parseFloat(l.descuento_monto || l.descuentoMonto || 0);
      const impuesto = parseFloat(l.impuesto_monto || l.impuestoMonto || 0);
      const total = parseFloat(l.monto_total_linea || l.montoTotalLinea || 0);

      if (impuesto > 0) {
        totalGravado += subtotal - descuento;
      } else {
        totalExento += subtotal - descuento;
      }
      totalDescuentos += descuento;
      totalImpuesto += impuesto;
      totalIVA += impuesto;
      totalVenta += total;

      // Sumar impuestos adicionales de cada línea
      const impuestosAd = l.impuestos_adicionales || l.impuestosAdicionales || [];
      impuestosAd.forEach(imp => {
        const codigo = imp.codigoImpuesto || imp.impuesto_codigo || imp.codigo;
        const porcentaje = parseFloat(imp.porcentaje || imp.tarifa || 0);
        const subtotalLinea = parseFloat(l.subtotal || l.monto_total || (l.cantidad * l.precio_unitario) || 0);
        const monto = Math.round((parseFloat(imp.impuesto_monto || imp.monto || 0) || (subtotalLinea * porcentaje / 100)) * 100) / 100;
        const descripcion = imp.descripcion || (codigo === '07' ? 'Impuesto 911' : codigo === '99' ? 'Cruz Roja' : `Impuesto ${codigo}`);

        if (!impuestosAdicionalesMap[codigo]) {
          impuestosAdicionalesMap[codigo] = { descripcion, monto: 0 };
        }
        impuestosAdicionalesMap[codigo].monto += monto;
        totalImpuesto += monto;
      });
    });

    // Crear array de desglose
    const impuestosDesglose = [];
    if (totalIVA > 0) {
      impuestosDesglose.push({ codigo: '01', descripcion: 'IVA', monto: totalIVA });
    }
    Object.entries(impuestosAdicionalesMap).forEach(([codigo, imp]) => {
      if (imp.monto > 0) {
        impuestosDesglose.push({ codigo, ...imp });
      }
    });

    // Calcular totales incluyendo impuestos adicionales
    const totalVentaNetaCalc = (documento.total_gravado || totalGravado) + (documento.total_exento || totalExento) - (documento.total_descuentos || totalDescuentos);
    const totalComprobanteCalc = totalVentaNetaCalc + totalImpuesto;

    return {
      totalGravado: documento.total_gravado || totalGravado,
      totalExento: documento.total_exento || totalExento,
      totalDescuentos: documento.total_descuentos || totalDescuentos,
      totalImpuesto: totalImpuesto,
      totalVenta: totalComprobanteCalc,
      totalVentaNeta: totalVentaNetaCalc,
      totalComprobante: totalComprobanteCalc,
      totalOtrosCargos: documento.total_otros_cargos || 0,
      impuestosDesglose: impuestosDesglose.length > 0 ? impuestosDesglose : undefined
    };
  };

  const totales = calcularTotales();

  const documentoForTemplate = {
    consecutivo: documento.consecutivo,
    clave: documento.clave,
    tipoDocumento: documento.tipo_documento || documento.tipoDocumento,
    fechaEmision: documento.fecha_emision || documento.fechaEmision,
    condicionVenta: documento.condicion_venta || documento.condicionVenta,
    estadoPago: documento.estado_pago || documento.estadoPago,
    periodoCobroTexto: documento.periodo_cobro_texto || documento.periodoCobroTexto,
    medioPago: documento.medio_pago || documento.medioPago,
    codigoMoneda: moneda,
    status: documento.status,
    notas: documento.notas,  // Comentarios y observaciones
    // Código de actividad económica del CLIENTE enviado en la FE (viene del XML, lo resuelve getById)
    codigoActividadReceptor: documento.codigoActividadReceptor,
    // Referencia para NC/ND
    referencia: documento.referencia ? {
      tipoDoc: documento.referencia.tipoDoc || documento.referencia.tipo_doc || documento.referencia_tipo_doc,
      numero: documento.referencia.numero || documento.referencia_numero,
      consecutivo: documento.referencia.consecutivo || documento.referencia_consecutivo,
      fecha: documento.referencia.fecha || documento.referencia_fecha,
      codigo: documento.referencia.codigo || documento.referencia_codigo,
      razon: documento.referencia.razon || documento.referencia_razon
    } : (documento.referencia_numero ? {
      tipoDoc: documento.referencia_tipo_doc,
      numero: documento.referencia_numero,
      consecutivo: documento.referencia_consecutivo,
      fecha: documento.referencia_fecha,
      codigo: documento.referencia_codigo,
      razon: documento.referencia_razon
    } : null)
  };

  // Format lineas for template
  const lineasForTemplate = lineas.map((l, idx) => {
    // Parse impuesto_exoneracion if it's a JSON string
    let impuestoExoneracion = l.impuesto_exoneracion || l.impuestoExoneracion;
    if (typeof impuestoExoneracion === 'string') {
      try { impuestoExoneracion = JSON.parse(impuestoExoneracion); } catch { impuestoExoneracion = null; }
    }

    // Parse impuestos adicionales
    const lineaSubtotal = parseFloat(l.subtotal || l.monto_total || (l.cantidad * l.precio_unitario) || 0);
    const impuestosAdicionales = (l.impuestos_adicionales || l.impuestosAdicionales || []).map(imp => {
      const porcentaje = parseFloat(imp.porcentaje || imp.tarifa || 0);
      const montoRaw = parseFloat(imp.impuesto_monto || imp.monto || 0) || (lineaSubtotal * porcentaje / 100);
      return {
        codigoImpuesto: imp.codigoImpuesto || imp.impuesto_codigo || imp.codigo,
        porcentaje,
        monto: Math.round(montoRaw * 100) / 100,
        descripcion: imp.descripcion || ''
      };
    });

    return {
      numeroLinea: l.numero_linea || l.numeroLinea || idx + 1,
      codigo: l.codigo || l.Codigo,
      sku: l.sku || l.codigo_comercial || l.codigoComercial,
      detalle: l.detalle || l.Detalle,
      cantidad: l.cantidad || l.Cantidad || 1,
      unidadMedida: l.unidad_medida || l.unidadMedida || 'Unid',
      precioUnitario: l.precio_unitario || l.precioUnitario || l.PrecioUnitario || 0,
      montoTotal: l.monto_total || l.montoTotal || 0,
      descuentoMonto: l.descuento_monto || l.descuentoMonto || 0,
      subtotal: l.subtotal || l.Subtotal || 0,
      impuestoTarifa: l.impuesto_tarifa || l.impuestoTarifa || 0,
      impuestoMonto: l.impuesto_monto || l.impuestoMonto || 0,
      impuestosAdicionales: impuestosAdicionales,
      montoTotalLinea: l.monto_total_linea || l.montoTotalLinea || l.MontoTotalLinea || 0,
      impuestoExoneracion: impuestoExoneracion
    };
  });

  // Prepare emisor for template
  const emisorForTemplate = {
    nombre: emisor?.nombre || emisor?.name,
    nombre_comercial: emisor?.nombre_comercial || emisor?.nombreComercial,
    identificacion: emisor?.identificacion,
    tipo_identificacion: emisor?.tipo_identificacion || emisor?.tipoIdentificacion || '02',
    email: emisor?.email,
    telefono_codigo: emisor?.telefono_codigo || '506',
    telefono_numero: emisor?.telefono_numero || emisor?.telefonoNumero,
    provincia: emisor?.provincia,
    canton: emisor?.canton,
    distrito: emisor?.distrito,
    otras_senas: emisor?.otras_senas || emisor?.otrasSenas,
    logo_url: emisor?.logo_url || emisor?.logoUrl,
    template_color: emisor?.template_color || emisor?.templateColor || '#F47920',
    terminos_condiciones: emisor?.terminos_condiciones || emisor?.terminosCondiciones
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-xl">
          <h2 className="text-lg font-semibold text-gray-900">Vista Previa - {documento.consecutivo}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700"
            >
              <Printer className="h-4 w-4" />
              Imprimir
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-100">
          <div className="mx-auto shadow-lg" style={{ width: '210mm' }}>
            <div ref={printRef}>
              <InvoiceTemplate
                documento={documentoForTemplate}
                emisor={emisorForTemplate}
                receptor={receptor}
                lineas={lineasForTemplate}
                totales={totales}
                esCotizacion={false}
                tipoCambio={tipoCambio}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
